import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadCustomerPaymentByIdUrl,
  zadCustomerPaymentCancelUrl,
  zadCustomerPaymentCustomerSummaryUrl,
  zadCustomerPaymentsUrl,
  zadCustomerPaymentSaleSummaryUrl,
  zadCustomersUrl,
  zadSalesUrl,
} from '../../api'
import {parseZadApiError} from '../zad/errors'
import {sanitizeZadCustomerPaymentWritePayload} from '../zad/customerPaymentHelpers'
import {buildZadListQuery} from '../zad/query'
import {
  CancelZadCustomerPaymentRequest,
  CreateZadCustomerPaymentRequest,
  FetchZadCustomerAccountSummaryRequest,
  FetchZadCustomerPaymentByIdRequest,
  FetchZadCustomerPaymentPickerRequest,
  FetchZadCustomerPaymentsRequest,
  FetchZadSalePaymentSummaryRequest,
} from './types'

export const fetchZadCustomerPayments = createAsyncThunk(
  'zadCustomerPayments/fetch',
  async (payload: FetchZadCustomerPaymentsRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadCustomerPaymentsUrl}?${buildZadListQuery(payload)}`)
      const {payments, total, totalPages} = res.data || {}
      return {
        success: true as const,
        payments: Array.isArray(payments) ? payments : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerPaymentById = createAsyncThunk(
  'zadCustomerPayments/fetchById',
  async (payload: FetchZadCustomerPaymentByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCustomerPaymentByIdUrl(payload.id))
      return {
        success: true as const,
        payment: res.data.payment,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadCustomerPayment = createAsyncThunk(
  'zadCustomerPayments/create',
  async (payload: CreateZadCustomerPaymentRequest, {rejectWithValue}) => {
    try {
      const body = sanitizeZadCustomerPaymentWritePayload(payload)
      const res = await axios.post(zadCustomerPaymentsUrl, body)
      return {
        success: true as const,
        payment: res.data.payment,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const cancelZadCustomerPayment = createAsyncThunk(
  'zadCustomerPayments/cancel',
  async (payload: CancelZadCustomerPaymentRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadCustomerPaymentCancelUrl(payload.id), {
        reason: String(payload.reason || ''),
      })
      return {
        success: true as const,
        payment: res.data.payment,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerAccountSummary = createAsyncThunk(
  'zadCustomerPayments/fetchCustomerSummary',
  async (payload: FetchZadCustomerAccountSummaryRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCustomerPaymentCustomerSummaryUrl(payload.customerId))
      const data = res.data || {}
      return {
        success: true as const,
        summary: {
          customer: data.customer,
          validatedSalesTotal: data.validatedSalesTotal,
          validatedReturnsTotal: data.validatedReturnsTotal,
          activePaymentsTotal: data.activePaymentsTotal,
          activeRefundsTotal: data.activeRefundsTotal,
          outstanding: data.outstanding,
          refundableCredit: data.refundableCredit,
          saleCount: data.saleCount,
          paymentCount: data.paymentCount,
          returnCount: data.returnCount,
          refundCount: data.refundCount,
        },
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSalePaymentSummary = createAsyncThunk(
  'zadCustomerPayments/fetchSaleSummary',
  async (payload: FetchZadSalePaymentSummaryRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCustomerPaymentSaleSummaryUrl(payload.saleId))
      const data = res.data || {}
      return {
        success: true as const,
        summary: {
          saleId: String(data.saleId || payload.saleId),
          saleNumber: data.saleNumber,
          customer: data.customer,
          total: data.saleTotal != null ? data.saleTotal : data.total,
          saleTotal: data.saleTotal != null ? data.saleTotal : data.total,
          validatedReturnsTotal: data.validatedReturnsTotal,
          netSaleTotal: data.netSaleTotal,
          allocated: data.allocated,
          outstanding: data.outstanding,
          status: data.status,
        },
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerPaymentPickerCustomers = createAsyncThunk(
  'zadCustomerPayments/fetchPickerCustomers',
  async (payload: FetchZadCustomerPaymentPickerRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadCustomersUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'createdAt',
          sortOrder: 'desc',
          isActive: 'true',
          search: payload?.search || '',
        })}`
      )
      const {customers} = res.data || {}
      return {
        success: true as const,
        customers: Array.isArray(customers) ? customers : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerPaymentPickerSales = createAsyncThunk(
  'zadCustomerPayments/fetchPickerSales',
  async (payload: FetchZadCustomerPaymentPickerRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadSalesUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'saleDate',
          sortOrder: 'desc',
          customer: payload.customer || '',
          status: 'VALIDATED',
          search: payload.search || '',
        })}`
      )
      const {sales} = res.data || {}
      return {
        success: true as const,
        sales: Array.isArray(sales) ? sales : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
